import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type {
  BorderColorProps,
  BorderStyleProps,
  BorderWidthProps,
  HeightProps,
  OverflowProps,
} from 'styled-system';
import {
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  height,
  maxWidth,
  minHeight,
  minWidth,
  overflow,
  space,
} from 'styled-system';

import h24LogoSvg from 'common/assets/images/home-24-logo.svg';
import { ButtonBase } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import RangeDisplay, { useGetRangeDisplayText } from 'common/components/RangeDisplay';
import { BodyRegularBR2 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import { useRouter } from 'common/providers/RouterProvider';
import { outlineCSS } from 'common/theme/styles';
import type { SpaceProps } from 'common/theme/system-types';
import type {
  BoxSizingProps,
  TextOverflowProps,
  ThemeMinHeightProps,
  WordBreakProps,
} from 'common/theme/system-utilities';
import { boxSizing, textOverflow, themeMinHeight, wordBreak } from 'common/theme/system-utilities';
import { getProductsListPath } from 'common/utils/url';

interface FilterChipProps {
  url: string;
  name: string;
  text: Nullable<string>;
  range?: ComponentProps<typeof RangeDisplay>;
  filterKey?: string;
}

const ICON_SIZE = 16;
const SHOP_FILTER_KEY = 'shop';
const HOME_24_SHOP_VALUE = 'home24 SE';

const FilterChipWrapper = styled(Flex)<
  ThemeMinHeightProps & BorderStyleProps & BorderWidthProps & BorderColorProps & BoxSizingProps
>`
  ${display};
  ${themeMinHeight};
  ${maxWidth};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border};
  ${borderRadius};
  ${space};
  ${boxSizing};
`;

const filterChipWrapperStyles: ComponentProps<typeof FilterChipWrapper> = {
  alignItems: 'center',
  justifyContent: 'center',
  themeMinHeight: '32px',
  maxWidth: '280px',
  borderRadius: 'x-large',
  mr: 'sp_0',
  pr: 'sp_8',
  mb: 'sp_0',
  borderStyle: 'solid',
  borderWidth: 'small',
  borderColor: 'sys.neutral.border.weak',
  boxSizing: 'border-box',
  backgroundColor: 'sys.neutral.background.default',
};

const FilterText = styled(BodyRegularBR2)<WordBreakProps & OverflowProps & TextOverflowProps>`
  ${wordBreak};
  ${overflow};
  ${textOverflow};
`;

const filterTextStyles: ComponentProps<typeof FilterText> = {
  as: 'span',
  mr: 'sp_8',
  pl: 'sp_12',
  py: 'sp_2',
  wordBreak: 'break-all',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const RemoveIcon = styled(Icon)<any>`
  ${minWidth};
  ${minHeight};
`;

const removeIconStyles = {
  minWidth: `${ICON_SIZE}px`,
  minHeight: `${ICON_SIZE}px`,
  size: `${ICON_SIZE}px`,
};

const Home24Logo = styled.img<HeightProps>`
  ${height};
`;

const ButtonHitArea = styled(ButtonBase)<SpaceProps>`
  ${space};
  cursor: pointer;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: none;
  }

  &:focus-visible > *:first-child {
    ${outlineCSS};
  }

  -webkit-tap-highlight-color: transparent;
`;

const buttonHitAreaStyles: ComponentProps<typeof ButtonHitArea> = {
  px: 'sp_4',
};

const FilterChipText = ({
  text,
  range,
}: {
  text: Nullable<string>;
  range?: ComponentProps<typeof RangeDisplay>;
}) => {
  if (!text) {
    if (!range) return null;
    const { key, ...rangeProps } = range as any;

    return <RangeDisplay key={key} {...rangeProps} />;
  }
  if (range) {
    const { key, ...rangeProps } = range as any;

    return (
      <>
        {text}: <RangeDisplay key={key} {...rangeProps} />
      </>
    );
  }

  return <>{text}</>;
};

const FilterChip = ({ text, range, url, name, filterKey }: FilterChipProps) => {
  const { r } = useLocalization();
  const router = useRouter();

  const onRemoveFilterClick = () => {
    const path = getProductsListPath(url);
    router.push(path, { scroll: true });
  };

  const rangeDisplayText = useGetRangeDisplayText({
    selectedMin: range?.selectedMin,
    selectedMax: range?.selectedMax,
    unit: range?.unit,
  });

  const isHome24Filter = filterKey === SHOP_FILTER_KEY && text === HOME_24_SHOP_VALUE;

  return (
    <ButtonHitArea
      {...buttonHitAreaStyles}
      onClick={onRemoveFilterClick}
      aria-label={r.t('h24_remove_filter_of', {
        filter: text ? `${name} ${text}` : `${name} ${rangeDisplayText}`,
      })}
      data-testid="filter-chip-button"
    >
      <FilterChipWrapper {...filterChipWrapperStyles} data-testid="filter-chip">
        {isHome24Filter ? (
          <Flex alignItems="center" pl="sp_12" mr="sp_8" py="sp_2">
            <Home24Logo src={h24LogoSvg} height={20} alt="" />
          </Flex>
        ) : (
          <FilterText {...filterTextStyles}>
            <FilterChipText text={text} range={range} />
          </FilterText>
        )}
        <RemoveIcon {...removeIconStyles} name="close" />
      </FilterChipWrapper>
    </ButtonHitArea>
  );
};

export default FilterChip;
